import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useColorScheme
} from '@mui/material';
import { JsonViewer, type JsonViewerTheme } from '@textea/json-viewer';
import { useAtom } from 'jotai';

import { CaseDetailCard } from '@/components/CaseDetailCard';
import { caseDetailsAtom } from '@/pages/Case/Case.store';
import type { InvestigationCycle } from '@/pages/Case/Case.types';

const CycleAccordion = ({
  cycle,
  defaultExpanded = false
}: {
  cycle: InvestigationCycle;
  defaultExpanded?: boolean;
}) => {
  const { mode: themeMode } = useColorScheme();

  return (
    <Accordion
      elevation={0}
      key={cycle.id}
      defaultExpanded={defaultExpanded}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary
        sx={{ backgroundColor: 'background.elevation3' }}
        expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
      >
        <Typography variant="h6">{`Cycle ${cycle.id}: Tool Calls`}</Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={(theme) => ({
          backgroundColor: 'background.elevation2',
          border: `1px solid ${theme.palette.divider}`
        })}
      >
        <JsonViewer
          theme={themeMode as JsonViewerTheme}
          value={cycle.toolCalls}
          rootName="Tool Calls"
          enableClipboard
          displayDataTypes={false}
          style={{ backgroundColor: 'transparent' }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const CaseRelevantData = () => {
  const [caseDetails] = useAtom(caseDetailsAtom);
  const { mode: themeMode } = useColorScheme();

  const hasContinuedAnalysis =
    (caseDetails?.continuedResearch || []).length > 0;

  return (
    <>
      <CaseDetailCard title="Alert Raw Data">
        <JsonViewer
          value={caseDetails?.raw_data.alert_data}
          theme={themeMode as JsonViewerTheme}
          displayDataTypes={false}
          style={{ backgroundColor: 'transparent' }}
        />
      </CaseDetailCard>

      {hasContinuedAnalysis && (
        <Typography variant="h3" sx={{ mt: 4, mb: 2 }}>
          Initial Analysis
        </Typography>
      )}

      {(caseDetails?.research?.investigationCycles || []).map(
        (cycle, index) => (
          <CycleAccordion
            key={cycle.id}
            cycle={cycle}
            defaultExpanded={index === 0}
          />
        )
      )}

      {hasContinuedAnalysis &&
        (caseDetails?.continuedResearch || []).map((analysis, index) => (
          <Box
            key={`${caseDetails?.id}-${analysis.analysisGuidance}`}
            sx={{ mt: 4 }}
          >
            <Typography variant="h3" sx={{ mt: 4, mb: 1 }}>
              Continued Analysis {index + 1}
            </Typography>
            <Typography
              sx={{ mb: 2, pr: 4, fontStyle: 'italic', fontSize: '1rem' }}
            >
              {analysis.analysisGuidance}
            </Typography>
            {(analysis?.investigationCycles || []).map((cycle) => (
              <CycleAccordion key={cycle.id} cycle={cycle} />
            ))}
          </Box>
        ))}
    </>
  );
};

export default CaseRelevantData;
